import React, { useState, lazy, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Provider, useSelector } from "react-redux";
import { configureStore } from "@reduxjs/toolkit";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import { Style, Fill, Stroke, Icon, Text } from "ol/style";
import GeoJSON from "ol/format/GeoJSON";
import { Map } from "ol";
import Feature from "ol/Feature";
import Polygon from "ol/geom/Polygon";
import Point from "ol/geom/Point";
import Circle from "ol/geom/Circle";
import { Translate } from "ol/interaction";
import { transform, fromLonLat } from "ol/proj";
import VerticalNavigation from "../../components/verticalNavigation/verticalNavigation.tsx";
import LayersComponent from "../../components/layers/layers.tsx";
import Heat from "../../components/heat/heat.tsx";
import Legend from "../../components/legend/Legend.tsx";
import Slider2 from "../../components/slider2/slider2.tsx";
import Slider3 from "../../components/slider3/slider3.tsx";
import SliderConnector from "../../components/sliderConnector/sliderConnector.tsx";
import Tooltip from "../../components/tooltip/tooltip.tsx";
import TruckCapacitySelector from "../../components/truckCapacitySelector/TruckCapacitySelector.tsx";
import Layout from "../../components/layout/Layout.jsx";
import SuitabilityCalculation from "../../components/site-suitablity-calculation/SuitabalityCalculation.jsx";
import { useAppContext } from "../../data/context.tsx";
import type { TagState } from "../../data/types";
import { useWizardTags } from "../../data/useWizardTags.ts";
import MapComponent from "../../external-library/ol-map/MapComponent.jsx";
import { getAllDistrictByRegionName } from "../../services/FeatureInfoService";
import "./plantLocation.css";
import plantMarkerIcon from "../../assets/map/plant-marker.png";
import RadioGroup from "../../components/radioGroup/radioGroup.tsx";
import AlertSnakBar from "../../components/dialogs/alert-message/AlertSnakBar";
import type { CountryDetails } from "../../data/types";

// Mock Redux slices so the map is not re-created when LayersComponent dispatches changes
const mockAuthReducer = (state = { user: null, isLoggedIn: false }, action: any) => state;
const mockLayerChangerSlice = (state = { layerName: null }, action: any) => state;
const mockAddedPointSlice = (state = { addedPointList: {} }, action: any) => state;
const createMockSelectedCountrySlice = (selectedCountry: string, countryDetails: CountryDetails) =>
  (state = { selectedCountry, countryDetails }, action: any) => state;

const PlantLocation: React.FC = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [selectedLayer, setSelectedLayer] = useState<string>("");
  const { state, setDistrict, updatePlantSetting, setPlantLocation: setContextPlantLocation } = useAppContext();
  const countryDetails = useSelector((globalState: any) => globalState.SelectedCountrySlice.countryDetails as CountryDetails);

  const mapStore = React.useMemo(() => configureStore({
    reducer: {
      auth: mockAuthReducer,
      LayerChangerSlice: mockLayerChangerSlice,
      SelectedCountrySlice: createMockSelectedCountrySlice(state.selectedCountry, countryDetails),
      AddedPointSlice: mockAddedPointSlice,
    },
  }), [state.selectedCountry, countryDetails]);

  // Map-related state
  const [mapInfo, setMapInfo] = useState<Map | null>(null);
  const [isActivefeatureInfo, setIsActivefeatureInfo] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [overlayLayer, setOverlayLayer] = useState<VectorLayer<any> | null>(null);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState<any>(null);
  const [districtInfo, setDistrictInfo] = useState<{
    district: string;
    wasteType: string;
    quantity: string;
    position: { x: number; y: number };
    mapCoordinate: [number, number]; // Store map coordinate instead of screen pixel
    clickPosition: { x: number; y: number }; // Current screen position of the map coordinate
  } | null>(null);
  const [showLayers, setShowLayers] = useState<boolean>(false);
  const [isZoomedToDistrict, setIsZoomedToDistrict] = useState<boolean>(false);
  const [isInPlantSettings, setIsInPlantSettings] = useState<boolean>(false);
  const [wasteQuantities, setWasteQuantities] = useState<{ min: number; max: number; quantities: number[] }>({ min: 0, max: 0, quantities: [] });
  const clickHandlerRef = React.useRef<any>(null);

  // Plant location selection state
  const [plantLocation, setPlantLocation] = useState<{ coordinate: [number, number]; pixel: [number, number] } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const [plantMarkerLayer, setPlantMarkerLayer] = useState<VectorLayer<any> | null>(null);
  const [translateInteraction, setTranslateInteraction] = useState<Translate | null>(null);
  const [plantName, setPlantName] = useState<string>("");
  // const [isEditingName, setIsEditingName] = useState<boolean>(false); // Removed, always editing
  const [editingName, setEditingName] = useState<string>(plantName);
  const [alertOpen, setAlertOpen] = useState({ open: false, type: "", message: "" });
  const [distanceCircleLayer, setDistanceCircleLayer] = useState<VectorLayer<any> | null>(null);
  const [nameInputShake, setNameInputShake] = useState<boolean>(false);
  const nameInputRef = React.useRef<HTMLInputElement | null>(null);
  const animationFrameRef = React.useRef<number | null>(null);

  // Function to generate random plant names
  const getRandomPlantName = () => {
    const randomNames = [
      "Green Power Plant",
      "EcoWaste Facility",
      "Sustainable Energy Hub",
      "BioMass Processing Center",
      "Clean Tech Plant",
      "Renewable Resource Facility",
      "GreenTech Industries",
      "EcoFriendly Processing",
      "Waste-to-Energy Plant",
      "Carbon Neutral Facility"
    ];
    return randomNames[Math.floor(Math.random() * randomNames.length)];
  };

  // Function to update plant name and refresh marker
  const updatePlantName = (newName: string) => {
    setPlantName(newName);

    // Update context and localStorage with the new name
    if (plantLocation) {
      setContextPlantLocation({
        coordinate: plantLocation.coordinate,
        name: newName  // This is the missing piece!
      });
    }

    // If there's an existing plant marker, remove it completely and recreate it
    if (plantMarkerLayer && plantLocation && mapInfo) {
      const currentCoordinate = plantLocation.coordinate;

      // Remove existing marker layer
      mapInfo.removeLayer(plantMarkerLayer);
      setPlantMarkerLayer(null);

      // Remove existing translate interaction
      if (translateInteraction) {
        mapInfo.removeInteraction(translateInteraction);
        setTranslateInteraction(null);
      }

      // Add a small delay to ensure cleanup completes, then recreate the marker
      setTimeout(() => {
        addPlantLocationToMap(currentCoordinate, newName);
      }, 50);
    }
  };

  /* Removed startEditingName as it is always editing */

  // Function to save the edited name (no layer recreate to avoid breaking drag)
  const saveEditedName = () => {
    const trimmed = editingName.trim();
    if (trimmed === plantName) return;
    setPlantName(trimmed);
    if (plantLocation) {
      setContextPlantLocation({
        coordinate: plantLocation.coordinate,
        name: trimmed,
      });
    }
  };

  // Function to cancel editing (revert to saved name)
  const cancelEditingName = () => {
    setEditingName(plantName);
  };

  // Memoize callbacks to prevent unnecessary re-renders
  const handleSetMapInfo = React.useCallback((map: Map) => {
    setMapInfo(map);
  }, []);

  const handleSetIsActivefeatureInfo = React.useCallback((active) => {
    setIsActivefeatureInfo(active);
  }, []);

  const handleSetSelectedLocation = React.useCallback((location) => {
    setSelectedLocation(location);
  }, []);

  const tags = useWizardTags();


  // Plant Settings options and tooltips
  const scaleOptions = ["Small", "Medium", "Large"];
  const operationalLifeOptions = ["10 Years", "15 Years", "20 Years"];
  const truckCapacityOptions = ["Manual", "<5m³", "5-10 m³", ">10m³"];

  const tooltipContent = {
    wasteCollectionEfficiency: {
      title: "Waste Collection Efficiency",
      content: "The effectiveness with which your plant collects target waste volumes, reflecting operational capacity and logistical performance in a specific area.",
    },
    plantCapacityFactor: {
      title: "Plant Capacity Factor",
      content: "The ratio of actual energy or material output of yout treatment facility to its maximum possible output over a set time frame.",
    },
    scaleOfPlant: {
      title: "Scale of Plant",
      content: "The overall size and throughput capacity of your bio-waste treatment facility, determining its material processing volume and infrastructure needs.",
    },
    operationalLife: {
      title: "Expected Operational Life of Plant",
      content: "The estimated number of years a facility is expected to function efficiently before requiring significant overhaul or decommissioning. This value is strictly connected and depends on the scale of plant.",
    },
    truckCapacity: {
      title: "Truck Capacity",
      content: "The scale of waste collection methods, ranging from manual collection (e.g., carts or bins) to mechanized approaches involving medium and large trucks, each varying in volume capacity and urban/rural suitability.",
    },
    averageDistance: {
      title: "Average Distance",
      content: "The typical distance waste travels from its collection point to the treatment facility, affecting transportation costs and emissions.",
    },
  };

  const WASTE_AVAILABILITY_LEGEND = {
    type: "gradient",
    title: "Waste Availability",
    leftText: "Low",
    rightText: "High",
    leftColor: "#C3E9D6",
    middleColor: "#7BCBA6",
    rightColor: "#146C4C",
    additionalColor: "#22AD7A",
  };

  const getLayerConfig = (layer: string, country: string) => {
    if (layer === "wasteAvailability") return WASTE_AVAILABILITY_LEGEND;
    const configs = {
      'ghana': {
        elevation: {
          type: "gradient",
          title: "Elevation",
          leftText: "Low",
          rightText: "High",
          leftColor: "#26207a",
          middleColor: "#fefebe",
          rightColor: "#8b2925",
          additionalColor: "#BF8A6A", // Additional 3rd color in the 4-color gradient
        },
        infrastructure: {
          type: "none", // Base map - no legend needed
          title: "Base Map",
        },
        suitability: {
          type: "gradient",
          title: "Suitability",
          leftText: "Low",
          rightText: "High",
          leftColor: "#e86933",
          middleColor: "#dd9103",
          rightColor: "#22ad7a",
        },
        climate: {
          type: "gradient",
          title: "Climate",
          leftText: "Unfavorable",
          rightText: "Favorable",
          leftColor: "#ff00ff",
          rightColor: "#0297a6",
        },
        population: {
          type: "gradient",
          title: "Population",
          leftText: "Low",
          rightText: "High",
          leftColor: "#d4d4d4",
          rightColor: "#0D1441",
        },
        resources: {
          type: "categorical",
          title: "Land Use",
          items: [
            { color: "#0071fe", label: "Water" },
            { color: "#267300", label: "Trees" },
            { color: "#ffffbe", label: "Rangeland" },
            { color: "#bdd2ff", label: "Flooded Vegetation" },
            { color: "#e59800", label: "Crops" },
            { color: "#ffffff", label: "Clouds" },
            { color: "#e60000", label: "Built Area" },
            { color: "#ffbee8", label: "Bare Ground" }
          ]
        },
      },
      'vietnam': {
        elevation: {
          type: "gradient",
          title: "Elevation",
          leftText: "Low",
          rightText: "High",
          leftColor: "#26207a",
          middleColor: "#fefebe",
          rightColor: "#8b2925",
          additionalColor: "#BF8A6A", // Additional 3rd color in the 4-color gradient
        },
        infrastructure: {
          type: "none", // Base map - no legend needed
          title: "Base Map",
        },
        suitability: {
          type: "gradient",
          title: "Suitability",
          leftText: "Low",
          rightText: "High",
          leftColor: "#b91414",
          middleColor: "#ffffbf",
          rightColor: "#379120",
        },
        climate: {
          type: "gradient",
          title: "Climate",
          leftText: "Unfavorable",
          rightText: "Favorable",
          leftColor: "#ff00ff",
          rightColor: "#0297a6",
        },
        population: {
          type: "gradient",
          title: "Population",
          leftText: "Low",
          rightText: "High",
          leftColor: "#d4d4d4",
          rightColor: "#0D1441",
        },
        resources: {
          type: "categorical",
          title: "Land Use",
          items: [
            { color: "#0071fe", label: "Water" },
            { color: "#267300", label: "Trees" },
            { color: "#ffffbe", label: "Rangeland" },
            { color: "#bdd2ff", label: "Flooded Vegetation" },
            { color: "#e59800", label: "Crops" },
            { color: "#ffffff", label: "Clouds" },
            { color: "#e60000", label: "Built Area" },
            { color: "#ffbee8", label: "Bare Ground" }
          ]
        },
      },
      'kenya': {
        elevation: {
          type: "gradient",
          title: "Elevation",
          leftText: "Low",
          rightText: "High",
          leftColor: "#30123b",
          middleColor: "#92ff47",
          rightColor: "#7a0403",
          additionalColor: "#857622", // Additional 3rd color in the 4-color gradient
        },
        infrastructure: {
          type: "none", // Base map - no legend needed
          title: "Base Map",
        },
        suitability: {
          type: "gradient",
          title: "Suitability",
          leftText: "Low",
          rightText: "High",
          leftColor: "#fe3c19",
          middleColor: "#f2fe2a",
          rightColor: "#147218",
        },
        climate: {
          type: "gradient",
          title: "Climate",
          leftText: "Unfavorable",
          rightText: "Favorable",
          leftColor: "#ff00ff",
          rightColor: "#0297a6",
        },
        population: {
          type: "gradient",
          title: "Population",
          leftText: "Low",
          rightText: "High",
          leftColor: "#d4d4d4",
          rightColor: "#0D1441",
        },
        resources: {
          type: "categorical",
          title: "Land Use",
          items: [
            { color: "#0071fe", label: "Water" },
            { color: "#267300", label: "Trees" },
            { color: "#ffffbe", label: "Rangeland" },
            { color: "#bdd2ff", label: "Flooded Vegetation" },
            { color: "#e59800", label: "Crops" },
            { color: "#ffffff", label: "Clouds" },
            { color: "#e60000", label: "Built Area" },
            { color: "#ffbee8", label: "Bare Ground" }
          ]
        },
      },
      'india': {
        elevation: {
          type: "gradient",
          title: "Elevation",
          leftText: "Low",
          rightText: "High",
          leftColor: "#30123b",
          middleColor: "#96fe44",
          rightColor: "#7a0403",
          additionalColor: "#857622", // Additional 3rd color in the 4-color gradient
        },
        infrastructure: {
          type: "none", // Base map - no legend needed
          title: "Base Map",
        },
        suitability: {
          type: "gradient",
          title: "Suitability",
          leftText: "Low",
          rightText: "High",
          leftColor: "#e86933",
          middleColor: "#dd9103",
          rightColor: "#22ad7a",
        },
        climate: {
          type: "gradient",
          title: "Climate",
          leftText: "Unfavorable",
          rightText: "Favorable",
          leftColor: "#ff00ff",
          rightColor: "#0297a6",
        },
        population: {
          type: "gradient",
          title: "Population",
          leftText: "Low",
          rightText: "High",
          leftColor: "#d4d4d4",
          rightColor: "#0D1441",
        },
        resources: {
          type: "categorical",
          title: "Land Use",
          items: [
            { color: "#0071fe", label: "Water" },
            { color: "#267300", label: "Trees" },
            { color: "#ffffbe", label: "Rangeland" },
            { color: "#bdd2ff", label: "Flooded Vegetation" },
            { color: "#e59800", label: "Crops" },
            { color: "#ffffff", label: "Clouds" },
            { color: "#e60000", label: "Built Area" },
            { color: "#ffbee8", label: "Bare Ground" }
          ]
        }
      }
    };


    return configs[country][layer] || { type: "none" } ;
  };

  const zoomToSelectedDistrict = () => {
    if (!mapInfo || !selectedDistrict) return;

    try {
      // Get district extent and zoom to it
      const districtExtent = selectedDistrict.getGeometry().getExtent();
      mapInfo.getView().fit(districtExtent, {
        duration: 1000,
        padding: [50, 50, 50, 50],
        maxZoom: 12
      });

      // Remove all district layers and initial overlay
      if (overlayLayer) {
        mapInfo.removeLayer(overlayLayer);
        setOverlayLayer(null);
      }

      // Also remove any layers that might be the districts layer
      const allLayers = mapInfo.getLayers().getArray();
      const layersToRemove = allLayers.filter(layer => {
        const layerName = layer.get('name') || layer.get('layerName');
        const properties = layer.get('properties');
        // More aggressive layer removal - remove any vector layers that might be districts
        return layerName === 'All Districts' ||
          layerName === 'Initial Dark Overlay' ||
          (properties && properties.name === 'All Districts') ||
          (layer instanceof VectorLayer && layer.getSource && layer.getSource().getFeatures &&
            layer.getSource().getFeatures().length > 0 &&
            layer.getSource().getFeatures().some(f => f.get && f.get('district')));
      });

      layersToRemove.forEach(layer => {
        const layerName = layer.get('name') || layer.get('layerName');
        console.log('Removing layer:', layerName);
        mapInfo.removeLayer(layer);
      });

      console.log('Layers remaining after cleanup:', mapInfo.getLayers().getArray().length);

      // Clear the overlayLayer state to prevent re-rendering
      setOverlayLayer(null);

      // Add gray overlay for non-selected areas
      setTimeout(() => {
        console.log('About to add gray overlay...');
        addGrayOverlay();
      }, 800); // Longer delay to ensure cleanup completes first

    } catch (error) {
      console.error("Error zooming to district:", error);
    }
  };

  const addGrayOverlay = () => {
    if (!mapInfo || !selectedDistrict) return;

    try {
      // Create a very large polygon covering the entire world
      const worldBounds = [
        [-20037508.34, -20037508.34], // Bottom left corner of Web Mercator
        [20037508.34, -20037508.34],  // Bottom right corner
        [20037508.34, 20037508.34],   // Top right corner
        [-20037508.34, 20037508.34],  // Top left corner
        [-20037508.34, -20037508.34]  // Close the ring
      ];

      // Get the selected district geometry coordinates
      const districtGeometry = selectedDistrict.getGeometry();
      let districtCoords = [];

      if (districtGeometry.getType() === 'Polygon') {
        districtCoords = districtGeometry.getCoordinates();
      } else if (districtGeometry.getType() === 'MultiPolygon') {
        // For MultiPolygon, take the largest polygon
        const polygons = districtGeometry.getCoordinates();
        let largestPolygon = polygons[0];
        let largestArea = 0;

        polygons.forEach(polygon => {
          const area = polygon[0].length; // Simple approximation
          if (area > largestArea) {
            largestArea = area;
            largestPolygon = polygon;
          }
        });
        districtCoords = largestPolygon;
      }

      // Create a polygon with hole (world bounds with district cutout)
      const polygonWithHole = [
        worldBounds,  // Outer ring (world bounds)
        districtCoords[0]  // Inner ring (district - creates hole)
      ];

      // Create feature with the polygon
      const overlayFeature = new Feature({
        geometry: new Polygon(polygonWithHole)
      });

      const overlaySource = new VectorSource({
        features: [overlayFeature]
      });

      const grayOverlayLayer = new VectorLayer({
        source: overlaySource,
        style: new Style({
          fill: new Fill({
            color: 'rgba(0, 0, 0, 0.8)' // Darker and more opaque
          }),
          stroke: new Stroke({
            color: 'transparent',
            width: 0
          })
        }),
        zIndex: 1500 // Very high z-index to ensure it's on top
      });

      grayOverlayLayer.set('layerName', 'Gray Overlay');
      mapInfo.addLayer(grayOverlayLayer);
      setOverlayLayer(grayOverlayLayer);

      console.log('Added gray overlay layer with color: rgba(0, 0, 0, 0.65)');
      console.log('Gray overlay z-index:', grayOverlayLayer.getZIndex());
      console.log('Total layers after adding gray overlay:', mapInfo.getLayers().getArray().length);

      // List all current layers to debug
      mapInfo.getLayers().getArray().forEach((layer, index) => {
        const layerName = layer.get('layerName') || layer.get('name') || `Layer ${index}`;
        const zIndex = layer.getZIndex();
        console.log(`Layer ${index}: ${layerName}, z-index: ${zIndex}`);
      });

      // Force map to render
      mapInfo.render();
      mapInfo.renderSync();

    } catch (error) {
      console.error('Error creating gray overlay:', error);
    }
  };

  const addInitialDarkOverlay = (districtFeatures: any[]) => {
    if (!mapInfo) return;

    try {
      // Create world bounds
      const worldBounds = [
        [-20037508.34, -20037508.34],
        [20037508.34, -20037508.34],
        [20037508.34, 20037508.34],
        [-20037508.34, 20037508.34],
        [-20037508.34, -20037508.34]
      ];

      // Create holes for all district geometries
      const holes: any[] = [];
      districtFeatures.forEach(feature => {
        const geometry = feature.getGeometry();
        if (geometry.getType() === 'Polygon') {
          holes.push(geometry.getCoordinates()[0]);
        } else if (geometry.getType() === 'MultiPolygon') {
          // Add all polygons from multipolygon
          const polygons = geometry.getCoordinates();
          polygons.forEach((polygon: any) => {
            holes.push(polygon[0]);
          });
        }
      });

      // Create polygon with multiple holes (world bounds with all districts as holes)
      const polygonWithHoles = [worldBounds, ...holes];

      const initialOverlayFeature = new Feature({
        geometry: new Polygon(polygonWithHoles)
      });

      const initialOverlaySource = new VectorSource({
        features: [initialOverlayFeature]
      });

      const initialOverlayLayer = new VectorLayer({
        source: initialOverlaySource,
        style: new Style({
          fill: new Fill({
            color: 'rgba(0, 0, 0, 0.65)' // Dark overlay with transparency
          }),
          stroke: new Stroke({
            color: 'transparent',
            width: 0
          })
        }),
        zIndex: 998 // Below districts but above base layers
      });

      initialOverlayLayer.set('layerName', 'Initial Dark Overlay');
      mapInfo.addLayer(initialOverlayLayer);

      console.log('Added initial dark overlay with', holes.length, 'district holes');

    } catch (error) {
      console.error('Error creating initial dark overlay:', error);
    }
  };

  // Function to convert Web Mercator to WGS84
  const webMercatorToWGS84 = (coordinate: [number, number]): [number, number] => {
    const [x, y] = coordinate;
    const lng = (x / 20037508.34) * 180;
    const lat = (y / 20037508.34) * 180;
    const latRad = (2 * Math.atan(Math.exp(lat * Math.PI / 180))) - (Math.PI / 2);
    const latDeg = latRad * 180 / Math.PI;
    return [lng, latDeg];
  };

  const handleNext = () => {
    if (selectedDistrict && !isZoomedToDistrict) {
      // Zoom to selected district and show layers
      zoomToSelectedDistrict();
      setShowLayers(true);
      setIsZoomedToDistrict(true);
    } else if (plantLocation && !isInPlantSettings) {
      // Save draft name before validating (handles click without blur)
      const effectiveName = (editingName || plantName || "").trim();
      if (!effectiveName) {
        setAlertOpen({ open: true, type: "error", message: "Plant name is required" });
        return;
      }
      if (effectiveName !== plantName) updatePlantName(effectiveName);
      // Enter plant settings mode
      setIsInPlantSettings(true);
    } else if (isInPlantSettings) {
      // Prepare payload for analysis
      let wasteQuantity = 0;
      if (selectedDistrict && state.selectedWasteType) {
        const properties = selectedDistrict.getProperties();
        const wasteTypeKey = state.selectedWasteType
          .toLowerCase()
          .replace(/\s/g, '')
          .replace(/[()]/g, '');

        const matchingKey = Object.keys(properties).find(key =>
          key.toLowerCase() === wasteTypeKey ||
          key.toLowerCase().replace(/[()]/g, '') === wasteTypeKey
        );

        if (matchingKey && properties[matchingKey] !== undefined) {
          wasteQuantity = parseFloat(properties[matchingKey]) || 0;
        }
      }

      // Convert coordinates
      let latitude = 0;
      let longitude = 0;
      if (plantLocation?.coordinate) {
        const [lng, lat] = webMercatorToWGS84(plantLocation.coordinate);
        latitude = lat;
        longitude = lng;
      }

      // Map scale of plant index to values (Assumption based on user sample)
      const scaleValues = [500, 2000, 10000];
      const scaleOfPlantValue = scaleValues[state.plantSettings.scaleOfPlant] || 1000;

      const payload = {
        wtfName: plantName || "New Plant",
        countryName: state.selectedCountry,
        district: selectedDistrict?.get('district'),
        rrrObjective: {
          Technology: state.focusAreas.technology,
          People: state.focusAreas.people,
          Environment: state.focusAreas.environment,
          Finance: state.focusAreas.finance,
          InstitutionsAndMarket: state.focusAreas.institutionsAndMarket
        },
        latitude: latitude,
        longitude: longitude,
        businessModels: state.selectedBusinessModels.map((model) => model.type),
        wasteCategory: state.selectedWasteCategory,
        wasteType: state.selectedWasteType,
        wasteQuantity: wasteQuantity,
        wasteCollectionEfficiency: state.plantSettings.wasteCollectionEfficiency,
        valueAddedProduct: state.selectedBusinessModel,
        rrrObjectiveId: 2, // Hardcoded based on user sample as dynamic ID is unavailable
        scaleOfPlantValue: scaleOfPlantValue,
        avgDistanceTraveled: state.plantSettings.averageDistance,
        expectedLifeOfPlant: [10, 15, 20][state.plantSettings.scaleOfPlant] || 10,
        plantCapacityFactor: state.plantSettings.plantCapacityFactor,
        truckCapacity: state.plantSettings.truckCapacity,
        fuelType: state.plantSettings.selectedFuel
      };

      console.log("Analysis Payload:", payload);

      // Navigate to results page from plant settings
      navigate("/mcda-results", { state: { payload } });
    }
  };

  const handleBack = () => {
    navigate("/business-model");
  };

  // Function to check if a coordinate is within the selected district
  const isCoordinateWithinDistrict = (coordinate: [number, number], district: any): boolean => {
    try {
      const geometry = district.getGeometry();

      // Create a point from the coordinate
      const point = new Point(coordinate);

      // Check if the point intersects with the district geometry
      return geometry.intersectsCoordinate(coordinate);
    } catch (error) {
      console.error('Error checking coordinate within district:', error);
      return false;
    }
  };

  // Plant location functions
  const addPlantLocationToMap = (coordinate: [number, number], customName?: string) => {
    if (!mapInfo) return;

    // Remove existing plant marker and translate interaction if any
    if (plantMarkerLayer) {
      mapInfo.removeLayer(plantMarkerLayer);
    }
    if (translateInteraction) {
      mapInfo.removeInteraction(translateInteraction);
    }

    // Create plant location feature
    const plantFeature = new Feature({
      geometry: new Point(coordinate),
      name: 'Plant Location'
    });

    // Style the plant marker with local icon and text label
    plantFeature.setStyle(
      new Style({
        image: new Icon({
          src: plantMarkerIcon,
          scale: 0.5, // Increased from 0.15 to make it more visible
          anchor: [0.5, 1],
          // Removed color property to preserve original PNG styling
          opacity: 1,
          crossOrigin: "anonymous",
        })
        // Text style removed as we use the input field overlay
      })
    );

    // Set a custom property to identify this as draggable
    plantFeature.set('draggable', true);

    const plantSource = new VectorSource({
      features: [plantFeature]
    });

    const newPlantLayer = new VectorLayer({
      source: plantSource,
      zIndex: 2000 // Very high z-index to show on top
    });

    newPlantLayer.set('layerName', 'Plant Location');
    mapInfo.addLayer(newPlantLayer);
    setPlantMarkerLayer(newPlantLayer);

    // Create translate interaction to allow dragging the plant marker (only if not in plant settings)
    if (!isInPlantSettings) {
      const newTranslateInteraction = new Translate({
        layers: [newPlantLayer], // Only allow translating the plant layer
      });

      // Listen for translate events to update plant location state and check boundaries
      newTranslateInteraction.on('translateend', (event) => {
        const translatedFeature = event.features.getArray()[0];
        if (translatedFeature) {
          const newCoordinate = translatedFeature.getGeometry().getCoordinates();

          // Check if the new coordinate is within the selected district
          if (selectedDistrict && isCoordinateWithinDistrict(newCoordinate as [number, number], selectedDistrict)) {
            const newPixel = mapInfo.getPixelFromCoordinate(newCoordinate);
            setPlantLocation({
              coordinate: newCoordinate as [number, number],
              pixel: newPixel as [number, number]
            });
            // Also save to context/localStorage
            setContextPlantLocation({
              coordinate: newCoordinate as [number, number],
              name: plantName
            });
            // Cancel any ongoing editing when the marker is moved
            // if (isEditingName) {
            //   cancelEditingName();
            // }
            console.log('Plant location moved to:', newCoordinate);
          } else {
            // Revert the move - put the feature back to its previous position
            translatedFeature.getGeometry().setCoordinates(plantLocation?.coordinate || coordinate);
            alert('Plant location must remain within the selected district boundaries.');
            console.log('Plant location move reverted - outside district boundaries');
          }
        }
      });

      // Add the interaction to the map
      mapInfo.addInteraction(newTranslateInteraction);
      setTranslateInteraction(newTranslateInteraction);
    }

    // Add pointer cursor when hovering over the plant marker (only if not in plant settings)
    if (!isInPlantSettings) {
      mapInfo.on('pointermove', (event) => {
        const features = mapInfo.getFeaturesAtPixel(event.pixel);
        const draggableFeature = features?.find(f => f.get && f.get('draggable'));
        mapInfo.getTargetElement().style.cursor = draggableFeature ? 'grab' : '';
      });
    }
  };

  const handleDragStart = (event: React.DragEvent) => {
    setIsDragging(true);
    // Set drag data to identify this as plant location drag
    event.dataTransfer.setData('text/plain', 'plant-location');
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDragPosition(null);
  };

  const handleMapDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleMapDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const dragData = event.dataTransfer.getData('text/plain');

    if (dragData === 'plant-location' && mapInfo) {
      // Get the map container to calculate correct coordinates
      const mapContainer = document.getElementById('map');
      if (mapContainer) {
        const rect = mapContainer.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        // Convert screen coordinates to map coordinates
        const coordinate = mapInfo.getCoordinateFromPixel([x, y]);
        if (coordinate) {
          // Check if the coordinate is within the selected district
          if (selectedDistrict && isCoordinateWithinDistrict(coordinate, selectedDistrict)) {
            setPlantLocation({ coordinate, pixel: [x, y] });
            // Also save to context/localStorage
            setContextPlantLocation({
              coordinate: coordinate as [number, number],
              name: plantName
            });
            addPlantLocationToMap(coordinate);
            console.log('Plant location set at:', coordinate);
          } else {
            // Show user feedback that location is outside district
            console.log('Plant location must be within the selected district');
            // Could add a toast notification here
            alert('Please place the plant location within the selected district boundaries.');
          }
        }
      }
    }

    setIsDragging(false);
    setDragPosition(null);
  };

  // Map-related functions
  const addNewPointToTheMap = React.useCallback(() => {
    // Placeholder function for adding points to map
    console.log("Adding new point to map");
  }, []);

  const allPoints = React.useMemo(() => [], []); // Empty array for now, could be populated with actual point data


  // Auto-load districts when map is ready and region is selected
  useEffect(() => {
    const loadDistrictsForRegion = async () => {
      if (!mapInfo || !state.selectedRegion || !state.selectedCountry || isLoadingDistricts) return;

      // Add a small delay to ensure map is fully initialized
      await new Promise(resolve => setTimeout(resolve, 500));

      setIsLoadingDistricts(true);

      try {
        const layerName = countryDetails.districtLayerName;

        const filterType = "region";

        console.log("Loading districts for region:" + state.selectedRegion);

        const response = await getAllDistrictByRegionName(layerName, state.selectedRegion, filterType);

        if (response.data && response.data.features) {
          const format = new GeoJSON();
          const incomingCrs = response.data.crs?.properties?.name || "EPSG:4326";

          // Read the features and reproject if needed
          const features = format.readFeatures(response.data, {
            dataProjection: incomingCrs,
            featureProjection: "EPSG:3857",
          });

          console.log("Loaded", features.length, "district features");
          console.log("Loaded Features", features);
          console.log("Map instance:", mapInfo);
          console.log("Current map layers before adding district layer:", mapInfo.getLayers().getArray());

          console.log("Selected waste type: " + state.selectedWasteType)

          if (features.length === 0) {
            console.warn("No districts matched for region:", state.selectedRegion, "with attribute:", filterType);
            return;
          }

          // Remove existing overlay layer
          if (overlayLayer) {
            mapInfo.removeLayer(overlayLayer);
          }

          const overlaySource = new VectorSource({ features });

          // Calculate waste quantities for color mapping
          const quantities: number[] = [];
          features.forEach(feature => {
            if (state.selectedWasteType) {
              const wasteTypeKey = state.selectedWasteType
                .toLowerCase()
                .replace(/\s/g, '')
                .replace(/[()]/g, '');


              const properties = feature.getProperties();

              console.log("Waste matching - Type:", wasteTypeKey, "| Available property keys:", Object.keys(properties));

              const matchingKey = Object.keys(properties).find(key =>
                key.toLowerCase() === wasteTypeKey ||
                key.toLowerCase().replace(/[()]/g, '') === wasteTypeKey
              );

              if (matchingKey && properties[matchingKey] !== undefined) {
                const quantity = parseFloat(properties[matchingKey]) || 0;
                quantities.push(quantity);
              } else {
                quantities.push(0);
              }
            } else {
              quantities.push(0);
            }
          });

          const minQuantity = Math.min(...quantities);
          const maxQuantity = Math.max(...quantities);

          // Update waste quantities state
          setWasteQuantities({ min: minQuantity, max: maxQuantity, quantities });

          const getQuantityBasedColor = (feature: any, index: number) => {
            const quantity = quantities[index] || 0;

            if (minQuantity === maxQuantity) {
              return '#22AD7A'; // Default to first color if all quantities are the same
            }

            // Normalize quantity to 0-1 range
            const normalized = (quantity - minQuantity) / (maxQuantity - minQuantity);

            // Color stops: 0% - #C3E9D6, 36% - #7BCBA6, 71% - #22AD7A, 100% - #146C4C
            if (normalized <= 0.36) {
              // Interpolate between #C3E9D6 and #7BCBA6
              const t = normalized / 0.36;
              return interpolateColor('#C3E9D6', '#7BCBA6', t);
            } else if (normalized <= 0.71) {
              // Interpolate between #7BCBA6 and #22AD7A
              const t = (normalized - 0.36) / (0.71 - 0.36);
              return interpolateColor('#7BCBA6', '#22AD7A', t);
            } else {
              // Interpolate between #22AD7A and #146C4C
              const t = (normalized - 0.71) / (1 - 0.71);
              return interpolateColor('#22AD7A', '#146C4C', t);
            }
          };

          const interpolateColor = (color1: string, color2: string, t: number): string => {
            const hex1 = color1.replace('#', '');
            const hex2 = color2.replace('#', '');

            const r1 = parseInt(hex1.substr(0, 2), 16);
            const g1 = parseInt(hex1.substr(2, 2), 16);
            const b1 = parseInt(hex1.substr(4, 2), 16);

            const r2 = parseInt(hex2.substr(0, 2), 16);
            const g2 = parseInt(hex2.substr(2, 2), 16);
            const b2 = parseInt(hex2.substr(4, 2), 16);

            const r = Math.round(r1 + (r2 - r1) * t);
            const g = Math.round(g1 + (g2 - g1) * t);
            const b = Math.round(b1 + (b2 - b1) * t);

            return `rgba(${r}, ${g}, ${b}, 1)`;
          };

          const newOverlayLayer = new VectorLayer({
            source: overlaySource,
            properties: { name: "All Districts" },
            visible: true,
            zIndex: 1000, // High z-index to ensure it's on top
            style: (feature, resolution) => {
              const isSelected = selectedDistrict && feature.getId() === selectedDistrict.getId();
              const featureIndex = features.findIndex(f => f.getId() === feature.getId());
              const fillColor = "rgba(255, 165, 0, 0.8)"
              const strokeColor = isSelected ? "#FF8C00" : "#FFFFFF";
              const strokeWidth = isSelected ? 3 : 2;

              return new Style({
                fill: new Fill({
                  color: fillColor,
                }),
                stroke: new Stroke({
                  color: strokeColor,
                  width: strokeWidth,
                }),
              });
            },
          });

          // Assign each feature a quantity-based color
          features.forEach((f, index) => {
            f.set("_quantityColor", getQuantityBasedColor(f, index));
          });

          // Add initial dark overlay covering everything except districts
          addInitialDarkOverlay(features);

          console.log("Adding district layer to map:", newOverlayLayer);
          console.log("Layer visible:", newOverlayLayer.getVisible());
          console.log("Layer zIndex:", newOverlayLayer.getZIndex());
          console.log("Features in layer:", newOverlayLayer.getSource().getFeatures().length);

          mapInfo.addLayer(newOverlayLayer);

          // Store reference to the districts layer (not overlay)
          // The overlay will be managed separately in zoomToSelectedDistrict
          setOverlayLayer(newOverlayLayer);

          console.log("Current map layers after adding district layer:", mapInfo.getLayers().getArray());
          console.log("Layer successfully added to map!");

          // Remove existing click handler if any
          if (clickHandlerRef.current) {
            mapInfo.un('click', clickHandlerRef.current);
            clickHandlerRef.current = null;
          }

          // Add click event handler for district selection
          const newClickHandler = (event: any) => {
            try {
              // Check if event and event.pixel exist
              if (!event || !event.pixel) {
                console.warn("Invalid click event:", event);
                return;
              }

              const features = mapInfo.getFeaturesAtPixel(event.pixel);
              if (features && features.length > 0) {
                const clickedFeature = features.find(f =>
                  f && f.get && f.get('district') // Check if this is a district feature
                );
                if (clickedFeature) {
                  const properties = clickedFeature.getProperties();
                  const districtName = clickedFeature.get('district');

                  // Get the click position in screen coordinates
                  const clickPosition = { x: event.pixel[0], y: event.pixel[1] };

                  // Convert screen coordinates to map coordinates
                  const mapCoordinate = mapInfo.getCoordinateFromPixel(event.pixel);

                  // Calculate info box position (offset from click point)
                  const infoBoxPosition = {
                    x: Math.min(clickPosition.x + 150, window.innerWidth - 300), // Ensure box stays in viewport
                    y: Math.max(clickPosition.y - 100, 50) // Ensure box stays in viewport
                  };

                  let quantity = "N/A";
                  let wasteTypeDisplay = state.selectedWasteType || "No waste type selected";

                  // If a waste type is selected, find the matching quantity
                  if (state.selectedWasteType) {
                    // Convert waste type name to match property key format (lowercase, no spaces, special chars)
                    const wasteTypeKey = state.selectedWasteType
                      .toLowerCase()
                      .replace(/\s/g, '')
                      .replace(/[()]/g, ''); // Remove parentheses

                    console.log("key" + wasteTypeKey);

                    // Find matching property (exact match or close match)
                    const matchingKey = Object.keys(properties).find(key =>
                      key.toLowerCase() === wasteTypeKey ||
                      key.toLowerCase().replace(/[()]/g, '') === wasteTypeKey
                    );

                    if (matchingKey && properties[matchingKey] !== undefined) {
                      quantity = `${properties[matchingKey]}`;
                    }
                  }

                  setSelectedDistrict(clickedFeature);
                  setDistrict(districtName);


                  // Set district info for the info box
                  setDistrictInfo({
                    district: districtName,
                    wasteType: wasteTypeDisplay,
                    quantity: quantity,
                    position: infoBoxPosition,
                    mapCoordinate: mapCoordinate,
                    clickPosition: clickPosition
                  });

                }
              } else {
                // Click outside any district - hide info box
                setDistrictInfo(null);
              }
            } catch (error) {
              console.error("Error in district click handler:", error);
            }
          };

          // Add the click event listener
          mapInfo.on('click', newClickHandler);
          clickHandlerRef.current = newClickHandler;

          // Force a refresh of the map view
          mapInfo.render();

          // Zoom to fit all districts, unless the user already zoomed into a district
          if (!isZoomedToDistrict) {
            try {
              const extent = overlaySource.getExtent();
              mapInfo.getView().fit(extent, {
                duration: 1000,
                padding: [50, 50, 50, 50],
              });
            } catch (e) {
              console.warn("Could not fit to extent:", e);
            }
          }
        }
      } catch (error) {
        console.warn("Failed to load districts for region:", error);
      } finally {
        setIsLoadingDistricts(false);
      }
    };

    loadDistrictsForRegion();
  }, [mapInfo, state.selectedRegion, state.selectedCountry, countryDetails]);

  // Update district layer styling when selected district changes
  useEffect(() => {
    if (overlayLayer && !isZoomedToDistrict) { // Don't re-render districts when zoomed
      console.log("Re-rendering district layer due to selection change");
      console.log("Selected district:", selectedDistrict ? selectedDistrict.get('district') : 'None');

      // Create a new style function that has access to current selectedDistrict
      const newStyleFunction = (feature: any) => {
        const isSelected = selectedDistrict && feature.getId() === selectedDistrict.getId();
        const fillColor = isSelected
          ? "rgba(255, 165, 0, 0.8)" // Orange for selected
          : feature.get("_quantityColor") || "hsla(210, 60%, 60%, 0.7)";
        const strokeColor = isSelected ? "#ffffffff" : "#FFFFFF";
        const strokeWidth = isSelected ? 3 : 2;

        console.log(`Feature ${feature.get('district')}: isSelected=${isSelected}, fillColor=${fillColor}`);

        return new Style({
          fill: new Fill({
            color: fillColor,
          }),
          stroke: new Stroke({
            color: strokeColor,
            width: strokeWidth,
          }),
        });
      };

      // Update the layer's style function
      overlayLayer.setStyle(newStyleFunction);

      // Force the layer to re-render
      overlayLayer.changed();
    }
  }, [selectedDistrict, overlayLayer]);

  // Update district info position when map view changes
  useEffect(() => {
    if (!mapInfo || !districtInfo) return;

    const updateDistrictInfoPosition = () => {
      if (districtInfo.mapCoordinate) {
        // Convert map coordinate back to current screen pixel
        const currentPixel = mapInfo.getPixelFromCoordinate(districtInfo.mapCoordinate);

        if (currentPixel) {
          const newClickPosition = { x: currentPixel[0], y: currentPixel[1] };

          // Calculate new info box position
          const newInfoBoxPosition = {
            x: Math.min(newClickPosition.x + 150, window.innerWidth - 300),
            y: Math.max(newClickPosition.y - 100, 50)
          };

          // Update the district info with new positions
          setDistrictInfo(prev => prev ? {
            ...prev,
            clickPosition: newClickPosition,
            position: newInfoBoxPosition
          } : null);
        }
      }
    };

    // Listen to map view changes (pan/zoom)
    mapInfo.getView().on('change', updateDistrictInfoPosition);

    return () => {
      if (mapInfo) {
        mapInfo.getView().un('change', updateDistrictInfoPosition);
      }
    };
  }, [mapInfo, districtInfo?.mapCoordinate]);

  // Update plant location pixel position when map view changes
  useEffect(() => {
    if (!mapInfo || !plantLocation) return;

    const updatePlantPosition = () => {
      // Look up current plant location coordinate
      const coordinate = plantLocation.coordinate;

      // Get new pixel position
      const pixel = mapInfo.getPixelFromCoordinate(coordinate);

      if (pixel) {
        // Only update if pixel changed significantly to avoid infinite loops
        // though with react state comparison it usually handles it.
        // But creating new object always triggers render.
        // We can check equality.
        setPlantLocation(prev => {
          if (!prev) return null;
          const [oldX, oldY] = prev.pixel;
          const [newX, newY] = pixel;
          if (Math.abs(oldX - newX) < 1 && Math.abs(oldY - newY) < 1) return prev;
          return { ...prev, pixel: [newX, newY] as [number, number] };
        });
      }
    };

    const view = mapInfo.getView();
    view.on('change', updatePlantPosition);
    // Also update immediately in case it's stale
    updatePlantPosition();

    return () => {
      view.un('change', updatePlantPosition);
    };
  }, [mapInfo, plantLocation?.coordinate]);

  // Sync editingName with plantName
  useEffect(() => {
    setEditingName(plantName);
  }, [plantName]);

  // Gentle shake + focus of name input when pin is dropped or moved (0.25s)
  useEffect(() => {
    if (plantLocation && !isInPlantSettings) {
      // Reset then re-trigger shake and focus on next tick (reliable on first drop and reposition)
      setNameInputShake(false);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setNameInputShake(true);
          nameInputRef.current?.focus();
          // Keep cursor ready even after reposition
        });
      });
      const t = setTimeout(() => setNameInputShake(false), 300);
      return () => {
        cancelAnimationFrame(raf);
        clearTimeout(t);
      };
    }
  }, [plantLocation?.coordinate?.[0], plantLocation?.coordinate?.[1]]);

  // Cleanup click handler and translate interaction on unmount
  useEffect(() => {
    return () => {
      if (mapInfo && clickHandlerRef.current) {
        mapInfo.un('click', clickHandlerRef.current);
        clickHandlerRef.current = null;
      }
      if (mapInfo && translateInteraction) {
        mapInfo.removeInteraction(translateInteraction);
        setTranslateInteraction(null);
      }
    };
  }, [mapInfo, translateInteraction]);

  // Remove translate interaction when entering plant settings
  useEffect(() => {
    if (isInPlantSettings && mapInfo && translateInteraction) {
      mapInfo.removeInteraction(translateInteraction);
      setTranslateInteraction(null);
      // Also remove cursor change
      mapInfo.getTargetElement().style.cursor = '';
    }
  }, [isInPlantSettings, mapInfo, translateInteraction]);


  // Restore plant location from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('plantLocation');
    if (stored && mapInfo && isZoomedToDistrict && !plantLocation) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.coordinate) {
          console.log('Restoring plant location from localStorage:', parsed);
          setPlantLocation({
            coordinate: parsed.coordinate,
            pixel: mapInfo.getPixelFromCoordinate(parsed.coordinate) as [number, number]
          });
          setPlantName(parsed.name || '');
          setEditingName(parsed.name || '');
          addPlantLocationToMap(parsed.coordinate, parsed.name);
        }
      } catch (e) {
        console.error('Error restoring plant location:', e);
      }
    }
  }, [mapInfo, isZoomedToDistrict]);



  // Add/update distance circle when in plant settings
  useEffect(() => {
    if (!mapInfo || !plantLocation) return;

    if (isInPlantSettings) {
      // Cancel any pending animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Use requestAnimationFrame to throttle updates
      animationFrameRef.current = requestAnimationFrame(() => {
        // Check if we already have a circle layer
        const allLayers = mapInfo.getLayers().getArray();
        const existingCircleLayer = allLayers.find(layer =>
          layer.get('layerName') === 'Distance Circle'
        ) as VectorLayer<any>;

        const center = plantLocation.coordinate;
        // Ensure minimum radius of 1km to prevent circle rendering issues
        const radiusInMeters = Math.max(1, state.plantSettings.averageDistance) * 1000; // Convert km to meters

        if (existingCircleLayer) {
          // Update existing circle's geometry
          const source = existingCircleLayer.getSource();
          const features = source.getFeatures();
          if (features.length > 0) {
            const circleFeature = features[0];
            const newCircleGeometry = new Circle(center, radiusInMeters);
            circleFeature.setGeometry(newCircleGeometry);
          }
        } else {
          // Clean up any orphaned circles first
          const orphanedCircles = allLayers.filter(layer =>
            layer.get('layerName') === 'Distance Circle'
          );
          orphanedCircles.forEach(layer => {
            mapInfo.removeLayer(layer);
          });

          // Create new circle layer only if it doesn't exist
          const circleGeometry = new Circle(center, radiusInMeters);

          const circleFeature = new Feature({
            geometry: circleGeometry
          });

          // Style the circle with semi-transparent fill
          circleFeature.setStyle(
            new Style({
              fill: new Fill({
                color: 'rgba(40, 83, 125, 0.5)' // #28537D with 50% opacity
              })
              // No stroke as requested
            })
          );

          // Create vector source and layer
          const circleSource = new VectorSource({
            features: [circleFeature]
          });

          const newCircleLayer = new VectorLayer({
            source: circleSource,
            zIndex: 1500 // Below plant marker but above other layers
          });

          newCircleLayer.set('layerName', 'Distance Circle');
          mapInfo.addLayer(newCircleLayer);
          setDistanceCircleLayer(newCircleLayer);
        }
      });
    } else {
      // Remove circle when not in plant settings
      const allLayers = mapInfo.getLayers().getArray();
      const existingCircleLayers = allLayers.filter(layer =>
        layer.get('layerName') === 'Distance Circle'
      );
      existingCircleLayers.forEach(layer => {
        mapInfo.removeLayer(layer);
      });
      setDistanceCircleLayer(null);
    }

    // Cleanup function
    return () => {
      // Cancel any pending animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // Only clean up when leaving plant settings or unmounting
      if (!isInPlantSettings && mapInfo) {
        const allLayers = mapInfo.getLayers().getArray();
        const circleLayersToRemove = allLayers.filter(layer =>
          layer.get('layerName') === 'Distance Circle'
        );
        circleLayersToRemove.forEach(layer => {
          mapInfo.removeLayer(layer);
        });
      }
    };
  }, [isInPlantSettings, mapInfo, plantLocation, state.plantSettings.averageDistance]); // Note: distanceCircleLayer not in deps to avoid loops

  return (
    <div className="plantLocation-container">
      <VerticalNavigation
        title={isInPlantSettings ? "Plant Settings" : "Plant Location"}
        description={isInPlantSettings ? "Set technical preferences for your waste treatment plant" : "Explore the Map to Identify a high-potential Location for your plant"}
        tags={tags}
        currentStep={isInPlantSettings ? 4 : 3}
        totalSteps={4}
        onMenuToggle={setIsMenuOpen}
        onBack={handleBack}
      />
      {!isMenuOpen && (
        <div className="plantLocation-content">
          <div className="plantLocation-inner">
            {!isInPlantSettings && (
              <div className="plantLocation-header-section">
                <h2 className="plantLocation-header-title">
                  {isZoomedToDistrict ? "Choose Plant Site" : "Select District"}
                </h2>
                {(() => {
                  const layerConfig = getLayerConfig(isZoomedToDistrict ? selectedLayer : "wasteAvailability", state.selectedCountry.toLowerCase());

                  if (layerConfig.type === "none") {
                    return null; // Hide heat/legend container for base map
                  } else if (layerConfig.type === "categorical") {
                    return <Legend title={layerConfig.title} items={layerConfig.items} />;
                  } else {
                    return <Heat {...layerConfig} />;
                  }
                })()}
              </div>
            )}

            {isInPlantSettings && (
              <div className="plantLocation-settings-section">
                <div className="plantLocation-settings-box">
                  <div className="plantLocation-settings-title-row">
                    <h3 className="plantLocation-settings-title">Set Up Your Plant</h3>
                  </div>

                  <div className="plantLocation-settings-group">
                    <Slider2
                      title="Waste Collection Efficiency"
                      value={state.plantSettings.wasteCollectionEfficiency}
                      onChange={(value) => updatePlantSetting("wasteCollectionEfficiency", value)}
                      min={0}
                      max={100}
                      unit="%"
                      tooltip={
                        <Tooltip
                          title={tooltipContent.wasteCollectionEfficiency.title}
                          content={tooltipContent.wasteCollectionEfficiency.content}
                          offsetY={-7}
                        />
                      }
                    />
                  </div>

                  <div className="plantLocation-settings-group">
                    <Slider2
                      title="Plant Capacity Factor"
                      value={state.plantSettings.plantCapacityFactor}
                      onChange={(value) => updatePlantSetting("plantCapacityFactor", value)}
                      min={0}
                      max={100}
                      unit="%"
                      tooltip={
                        <Tooltip
                          title={tooltipContent.plantCapacityFactor.title}
                          content={tooltipContent.plantCapacityFactor.content}
                          offsetY={-7}
                        />
                      }
                    />
                  </div>

                  <div className="plantLocation-settings-group">
                    <SliderConnector
                      type="3-3"
                      title3="Scale of Plant"
                      options3={scaleOptions}
                      selectedIndex3={state.plantSettings.scaleOfPlant}
                      disableSecondSlider={true}
                      onChange3={(value) => updatePlantSetting("scaleOfPlant", value)}
                      title3Second="Expected Operational Life of Plant"
                      options3Second={operationalLifeOptions}
                      selectedIndex3Second={state.plantSettings.operationalLife}
                      onChange3Second={(value) => updatePlantSetting("operationalLife", value)}
                      tooltipTitle3={tooltipContent.scaleOfPlant.title}
                      tooltipContent3={tooltipContent.scaleOfPlant.content}
                      tooltipTitle3Second={tooltipContent.operationalLife.title}
                      tooltipContent3Second={tooltipContent.operationalLife.content}
                    />
                  </div>

                  <div className="plantLocation-settings-group">
                    <TruckCapacitySelector
                      title="Truck Capacity"
                      selectedIndex={state.plantSettings.truckCapacity}
                      onChange={(value) => updatePlantSetting("truckCapacity", value)}
                      tooltip={
                        <Tooltip
                          title={tooltipContent.truckCapacity.title}
                          content={tooltipContent.truckCapacity.content}
                          offsetY={-7}
                        />
                      }
                    />
                  </div>

                  <div className="plantLocation-settings-group">
                    <Slider2
                      title="Average Distance"
                      value={state.plantSettings.averageDistance}
                      onChange={(value) => updatePlantSetting("averageDistance", value)}
                      min={1}
                      max={40}
                      unit="km"
                      tooltip={
                        <Tooltip
                          title={tooltipContent.averageDistance.title}
                          content={tooltipContent.averageDistance.content}
                          offsetY={-7}
                        />
                      }
                    />
                  </div>

                  <div className="plantSettings-slider-group">

                    <RadioGroup
                      title="Fuel Type"
                      options={["Petrol/Gasoline", "Diesel", "Fuel Oil", "LPG", "Natural Gas", "Hydrogen"]}
                      name="fuel"
                      value={state.plantSettings.selectedFuel}
                      onChange={(value) => updatePlantSetting("selectedFuel", value)}
                      enabledOption="Diesel"
                      tooltipTitle="Fuel Type"
                      tooltipContent="Only Diesel is available in this version" />
                  </div>
                </div>
              </div>
            )}

            {showLayers && !isInPlantSettings && (
              <LayersComponent
                onLayerSelect={setSelectedLayer}
                mapInstance={mapInfo}
                selectedCountry={state.selectedCountry.toLowerCase()} />
            )}

            <div
              className="plantLocation-map-container"
              onDragOver={handleMapDragOver}
              onDrop={handleMapDrop}
            >
                <Provider store={mapStore}>
                  <MapComponent
                    setMapInfo={setMapInfo}
                    setIsActivefeatureInfo={setIsActivefeatureInfo}
                    isActivefeatureInfo={isActivefeatureInfo}
                    addNewPointToTheMap={() => {}}
                    allPoints={[]}
                    selectedLocation={selectedLocation}
                    setSelectedLocation={setSelectedLocation}
                  />
                </Provider>

              {/* Plant Name Editor Overlay - always show when plant location exists */}
              {plantLocation && !isInPlantSettings && (
                <div
                  className={nameInputShake ? "plant-location-name-shake" : undefined}
                  style={{
                    position: 'absolute',
                    left: `${plantLocation.pixel[0] - 80}px`, // Center the input
                    top: `${plantLocation.pixel[1] - 90}px`, // Position above the marker
                    zIndex: 2001, // Above the plant marker
                    backgroundColor: 'rgba(255,255,255, 1)',
                    padding: '8px',
                    borderRadius: '5px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
                  }}
                >
                  <input
                    ref={nameInputRef}
                    autoFocus
                    spellCheck={false}
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        saveEditedName();
                        // Optional: Remove focus on Enter
                        (e.target as HTMLInputElement).blur();
                      } else if (e.key === 'Escape') {
                        cancelEditingName();
                      }
                    }}
                    onBlur={saveEditedName}
                    style={{
                      width: '150px',
                      padding: '4px 8px',
                      border: 'none',
                      borderRadius: '3px',
                      fontSize: '12px',
                      color: '#28537D',
                      outline: 'none',
                      textAlign: 'center',
                      background: 'none'
                    }}
                    placeholder=" Name your plant"
                  />
                </div>
              )}


              {/* District Info Box - only show when not zoomed to district */}
              {districtInfo && !isZoomedToDistrict && (
                <>
                  {/* Connecting line */}
                  <svg
                    className="district-info-line"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'none',
                      zIndex: 1001
                    }}
                  >
                    <line
                      x1={districtInfo.clickPosition.x}
                      y1={districtInfo.clickPosition.y}
                      x2={districtInfo.position.x}
                      y2={districtInfo.position.y + 50} // Connect to middle of info box
                      stroke="white"
                      strokeWidth="3"
                      strokeDasharray="5,5"
                    />
                    {/* Dot at click point */}
                    <circle
                      cx={districtInfo.clickPosition.x}
                      cy={districtInfo.clickPosition.y}
                      r="4"
                      fill="#1e3a8a"
                      stroke="#ffffff"
                      strokeWidth="2"
                    />
                  </svg>

                  {/* Info Box */}
                  <div
                    className="district-info-box"
                    style={{
                      position: 'absolute',
                      left: `${districtInfo.position.x}px`,
                      top: `${districtInfo.position.y}px`,
                      zIndex: 1002
                    }}
                  >
                    <div className="district-info-content">
                      <h3 className="district-info-title">{districtInfo.district}</h3>
                      <p className="district-info-waste-type">{districtInfo.wasteType} (MT/Km²)</p>
                      <p className="district-info-quantity">{districtInfo.quantity}</p>
                    </div>
                    <button
                      className="district-info-close"
                      onClick={() => setDistrictInfo(null)}
                      aria-label="Close district info"
                    >
                      ×
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          {/* Plant Location Selection Box - only show when zoomed to district and not in plant settings */}
          {isZoomedToDistrict && !isInPlantSettings && (
            <div className="plant-location-selection-container">
              <span className="plant-location-hint">Drag me to the map!</span>
              <div className="plant-location-selection-box">
                <div className="plant-location-drag-item">
                  <div className="plant-location-icon-container">
                    <img
                      src={plantMarkerIcon}
                      alt="Plant Location"
                      className={`plant-location-icon ${isDragging ? 'dragging' : ''}`}
                      draggable
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                    />
                  </div>
                  <span className="plant-location-text">Location</span>
                </div>
              </div>
            </div>
          )}

          <div className="plantLocation-button-container">
            {(() => {
              const isFormValid = state.plantSettings.selectedFuel !== "no fuel" && state.plantSettings.selectedFuel !== "";

              return (
                <button
                  className={`plantLocation-next-button ${isInPlantSettings
                    ? (isFormValid ? "plantLocation-next-button--launch" : "plantLocation-next-button--disabled")
                    : ((!isZoomedToDistrict && selectedDistrict) ||
                      (isZoomedToDistrict && !isInPlantSettings && plantLocation && (editingName || plantName || "").trim()))
                      ? "plantLocation-next-button--active"
                      : "plantLocation-next-button--disabled"
                    }`}
                  onClick={handleNext}
                  disabled={
                    isInPlantSettings
                      ? !isFormValid
                      : (!isZoomedToDistrict && !selectedDistrict) ||
                      (isZoomedToDistrict && !isInPlantSettings && (!plantLocation || !(editingName || plantName || "").trim()))
                  }
                >
                  {isInPlantSettings ? "Launch Analysis →" : isZoomedToDistrict ? "Plant Settings →" : "Next →"}
                </button>
              );
            })()}
          </div>
        </div>
      )}
      <AlertSnakBar aleartOpen={alertOpen} setAlertOpen={setAlertOpen} />
    </div>
  );
};

export default PlantLocation;
